import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HERO } from "@/content/landing";
import { HeroIllustration } from "./HeroIllustration";

export function Hero() {
  const reduce = useReducedMotion();
  const initial = reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 };
  const animate = { opacity: 1, y: 0 };

  return (
    <section
      data-testid="landing-hero"
      className="relative w-full overflow-hidden pt-32 sm:pt-40"
    >
      {/* Decorative grid */}
      <div
        aria-hidden="true"
        className="sema-grid pointer-events-none absolute inset-0 opacity-40"
      />
      {/* Accent glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[760px] -translate-x-1/2 rounded-full blur-3xl opacity-20 dark:opacity-30"
        style={{
          background:
            "radial-gradient(closest-side, hsl(var(--accent)), transparent)",
        }}
      />

      <div className="relative mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 pb-24 lg:grid-cols-12 lg:gap-16 lg:pb-32">
        <div className="lg:col-span-7">
          <motion.div
            initial={initial}
            animate={animate}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-muted-foreground backdrop-blur"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
            {HERO.eyebrow}
          </motion.div>

          <motion.h1
            data-testid="landing-hero-title"
            initial={initial}
            animate={animate}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.08 }}
            className="font-display text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl"
          >
            Ask your data anything.{" "}
            <span className="sema-gradient-text">Trust the answer.</span>
          </motion.h1>

          <motion.p
            data-testid="landing-hero-subtitle"
            initial={initial}
            animate={animate}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.16 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
          >
            {HERO.subtitle}
          </motion.p>

          <motion.div
            initial={initial}
            animate={animate}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.24 }}
            className="mt-9 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
          >
            <Link to={HERO.primaryCta.href}>
              <Button
                data-testid="landing-hero-cta-primary"
                size="lg"
                className="group h-12 rounded-full sema-cta-gradient px-6 font-mono text-xs uppercase tracking-[0.22em] text-white shadow-lg shadow-sema-primary/30 transition-transform hover:-translate-y-0.5"
              >
                {HERO.primaryCta.label}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Link>
            <Link to={HERO.secondaryCta.href}>
              <Button
                data-testid="landing-hero-cta-secondary"
                variant="outline"
                size="lg"
                className="h-12 rounded-full border-border/80 px-6 font-mono text-xs uppercase tracking-[0.22em] text-foreground hover:bg-muted"
              >
                {HERO.secondaryCta.label}
              </Button>
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={reduce ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.25 }}
          className="lg:col-span-5"
          data-testid="landing-hero-illustration"
        >
          <HeroIllustration />
        </motion.div>
      </div>
    </section>
  );
}
